import { useField } from 'formik'
import React, { useCallback } from 'react'

import { MultiItemSelectProps } from './multiItemSelect'

type ItemType<C extends MultiItemSelectProps<any>> = C extends MultiItemSelectProps<infer T> ? T : never

type FormikConnectedItemSelectArgs<C extends MultiItemSelectProps<any>> = {
  component: React.FunctionComponent<C>
  useItems?: (uuids: string[]) => ItemType<C>[]
  mapItems?: (items: ItemType<C>[]) => any[]
}

export default function formikConnectedMultiItemSelect<P extends MultiItemSelectProps<any>>({
  component,
  useItems = (items) => items as any,
  mapItems = (items) => items,
}: FormikConnectedItemSelectArgs<P>): React.FunctionComponent<
  { name: string; required?: boolean } & Omit<P, 'items' | 'setItems' | 'name' | 'touched' | 'error'>
> {
  return ({ name, required, ...selectProps }) => {
    const validate = useCallback(
      (value: any) => (!required || value.length ? undefined : 'Please fill out this field'),
      [required]
    )

    const [field, meta, helpers] = useField({ name, validate })

    const items = useItems(field.value || [])

    const handleSelectItems = useCallback(
      (update: React.SetStateAction<ItemType<P>[]>) => {
        const next = typeof update === 'function' ? update(items) : update
        helpers.setValue(mapItems(next), true)
      },
      [helpers.setValue, items]
    )

    const handleBlur = useCallback(() => field.onBlur(name)({ target: {} }), [name, field.onBlur])

    return React.createElement(component, {
      ...selectProps,
      ...field,
      items,
      setItems: handleSelectItems,
      touched: meta.touched,
      error: meta.error,
      onBlur: handleBlur,
    } as any)
  }
}
